import { InfoIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import {
  awaitingReasonKey,
  closeReasonKey,
  nextActorKey,
  phaseKey,
  type NegotiationListItem,
} from '@entities/negotiation';
import { ROUTES } from '@shared/config/routes';
import { moneyOf } from '@shared/lib/money';
import { percentValue } from '@shared/lib/percent';
import { Badge, Media } from '@shared/ui';

import { NegotiationStatusBadge } from './NegotiationStatusBadge';
import styles from './NegotiationListCard.module.css';

interface NegotiationListCardProps {
  item: NegotiationListItem;
}

/**
 * Карточка списка переписок (FE-172, MVP_PRD §22.1).
 *
 * Показывает то и только то, что перечислено в §22.1: автомобиль, автохаус и
 * город, статус, цену объявления, лучшее предложение с разницей, дату
 * последнего события и **чей ход**.
 *
 * Чей ход считает backend (`next_actor`), а не карточка по статусу: два
 * вывода одного правила расходятся ровно тогда, когда появляется новый статус.
 *
 * Оценки автохауса на карточке нет и не будет — ни звёзд, ни «надёжный»
 * (MVP_PRD §37.1).
 *
 * Редизайн 2026-10: статус — капсула с иконкой и текстом
 * (`NegotiationStatusBadge`), причина ожидания — «требует внимания» с иконкой,
 * причина закрытия — нейтральная строка: закрытие переписки — факт, а не
 * тревога (DESIGN_SYSTEM §2.4).
 */
export function NegotiationListCard({ item }: NegotiationListCardProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';
  const reason = item.awaiting_reason ?? item.close_reason;

  return (
    <article className={styles.card}>
      <Link to={ROUTES.negotiation(item.negotiation_id)} className={styles.link}>
        <div className={styles.cover}>
          {/* `Media` сам рисует честную заглушку, когда изображения нет:
              «сломанной» картинки в интерфейсе не бывает. */}
          <Media
            src={item.vehicle.cover_image?.card_url ?? undefined}
            alt={item.vehicle.cover_image?.alt ?? item.vehicle.title}
            ratio="4 / 3"
          />
        </div>

        <div className={styles.body}>
          <p className={styles.badges}>
            <NegotiationStatusBadge status={item.status} />
            <Badge tone="neutral">{t(phaseKey(item.phase))}</Badge>
          </p>

          <h3 className={styles.title}>
            {item.vehicle.make} {item.vehicle.model}
            {item.vehicle.variant === null ? null : ` · ${item.vehicle.variant}`}
          </h3>

          <p className={styles.seller}>
            {item.vehicle.seller_name ?? t('negotiation.list.unknownSeller')}
            {item.vehicle.seller_city === null ? null : ` · ${item.vehicle.seller_city}`}
          </p>

          <dl className={styles.prices}>
            <div>
              <dt>{t('negotiation.list.listingPrice')}</dt>
              <dd>{moneyOf(item.listing_price, locale)}</dd>
            </div>
            <div>
              <dt>{t('negotiation.list.bestOffer')}</dt>
              <dd
                className={
                  item.best_offer === null || item.best_offer.price === null
                    ? styles.noData
                    : undefined
                }
              >
                {item.best_offer?.price
                  ? moneyOf(item.best_offer.price, locale)
                  : t('common.noData')}
                {item.best_offer?.delta_vs_listing_percent === null ||
                item.best_offer === null ? null : (
                  <span className={styles.delta}>
                    {' '}
                    {percentValue(item.best_offer.delta_vs_listing_percent, locale)} %
                  </span>
                )}
              </dd>
            </div>
          </dl>

          {/* Чей ход — главный вопрос списка: он отвечает, ждать или делать. */}
          <p className={styles.actor}>{t(nextActorKey(item.next_actor))}</p>

          {reason === null ? null : item.awaiting_reason === null ? (
            <p className={styles.closed}>
              <InfoIcon size={16} aria-hidden="true" className={styles.reasonIcon} />
              {t(closeReasonKey(item.close_reason ?? ''))}
            </p>
          ) : (
            <p className={styles.reason}>
              <WarningCircleIcon size={16} aria-hidden="true" className={styles.reasonIcon} />
              {t(awaitingReasonKey(item.awaiting_reason))}
            </p>
          )}

          <p className={styles.meta}>
            {t('negotiation.list.lastEvent', {
              when: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
                new Date(item.last_event_at),
              ),
            })}
          </p>
        </div>
      </Link>
    </article>
  );
}
